/**
 * Collects required product stitching measurements and keeps them associated
 * with the product form as Shopify line-item properties.
 */
class StitchingService extends HTMLElement {
  #form;
  #abortController = new AbortController();

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    this.#form = document.getElementById(this.dataset.formId);
    if (!(this.#form instanceof HTMLFormElement)) return;

    const { signal } = this.#abortController;
    this.addEventListener('input', this.#updateState, { signal });
    this.addEventListener('change', this.#updateState, { signal });
    this.addEventListener('click', this.#handleStitchingSubmit, { signal });
    this.addEventListener('invalid', this.#handleInvalid, { capture: true, signal });
    document.addEventListener('click', this.#handleAddToCartClick, { capture: true, signal });
    this.#form.closest('product-form-component')?.addEventListener('cart:update', this.#handleCartUpdate, { signal });
    this.#updateState();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #getInputs() {
    return Array.from(this.querySelectorAll('[data-stitching-input]'));
  }

  #isComplete = () => this.#getInputs().every((input) => input.value.trim().length > 0 && input.validity.valid);

  #updateState = () => {
    const complete = this.#isComplete();
    this.dataset.complete = String(complete);

    this.#getAddToCartButtons().forEach((button) => {
      if (button.disabled) return;

      button.toggleAttribute('data-stitching-measurements-incomplete', !complete);

      const buttonText = button.querySelector('.add-to-cart-text__content');
      if (!buttonText) return;

      // The sticky button keeps quantity in a sibling span, so update only
      // its label span and leave the quantity display intact.
      const buttonLabel = buttonText.firstElementChild || buttonText;

      if (!buttonLabel.dataset.defaultText) buttonLabel.dataset.defaultText = buttonLabel.textContent.trim();
      buttonLabel.textContent = complete ? buttonLabel.dataset.defaultText : 'Add Measurements';
    });

    this.#form?.querySelectorAll('.accelerated-checkout-block').forEach((container) => {
      container.toggleAttribute('hidden', !complete);
    });

    this.querySelectorAll('[data-stitching-submit]').forEach((button) => {
      button.disabled = !complete;
    });

  };

  #getAddToCartButtons() {
    const buttons = Array.from(this.#form?.querySelectorAll('[ref="addToCartButton"]') || []);
    const productId = this.#form?.closest('product-form-component')?.dataset.productId;
    if (!productId) return buttons;

    document.querySelectorAll('sticky-add-to-cart').forEach((stickyAddToCart) => {
      if (stickyAddToCart.dataset.productId !== productId) return;

      const stickyButton = stickyAddToCart.querySelector('[ref="addToCartButton"]');
      if (stickyButton instanceof HTMLButtonElement) buttons.push(stickyButton);
    });

    return buttons;
  }

  #handleAddToCartClick = (event) => {
    if (this.#isComplete() || !(event.target instanceof Element)) return;

    const button = event.target.closest('[ref="addToCartButton"]');
    if (!button || !this.#form?.contains(button)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#openAndFocus();
  };

  #handleStitchingSubmit = (event) => {
    if (!this.#isComplete() || !(event.target instanceof Element)) return;

    const button = event.target.closest('[data-stitching-submit]');
    if (!button || !this.contains(button)) return;

    this.querySelector('dialog-component')?.closeDialog?.();
    this.#form
      ?.closest('product-form-component')
      ?.querySelector('add-to-cart-component')
      ?.animateAddToCart?.();
  };

  #handleCartUpdate = (event) => {
    if (event.detail?.data?.source !== 'product-form-component' || event.detail?.data?.didError) return;

    this.querySelectorAll('[data-stitching-field]').forEach((input) => {
      input.value = '';
    });
    this.#updateState();
  };

  #handleInvalid = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-stitching-input]')) return;

    event.preventDefault();
    this.#openAndFocus();
  };

  #openAndFocus() {
    const message = this.querySelector('[data-stitching-message]');
    if (message) message.hidden = false;

    const dialogComponent = this.querySelector('dialog-component');
    dialogComponent?.showDialog?.();

    requestAnimationFrame(() => {
      const firstInvalidInput = this.#getInputs().find((input) => !input.validity.valid);
      firstInvalidInput?.focus();
    });
  }
}

if (!customElements.get('stitching-service')) {
  customElements.define('stitching-service', StitchingService);
}
